/**
 * @format
 */
import { Component, EventEmitter, inject, Input, Output, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { MaxLength } from 'common-typescript/constants';
import { distinctUntilChanged } from 'rxjs';
import { map } from 'rxjs/operators';
import { max, maxLength, min, required } from 'sis-components/form/form-validators';
import { getLabelState } from 'sis-components/form/formUtils';
import { SisFormBuilder } from 'sis-components/form/sis-form-builder.service';
import {
    getValidationErrorSummaryDetail,
    ValidationErrorSummaryDetail,
} from 'sis-components/form/validation-error-summary/validation-error-summary.component';
import { Option } from 'sis-components/select/dropdown-select/dropdown-select.component';
import { AutomaticallyFilledExtensionApplicationFields, ExtensionApplicationFormData, PlanData } from '../types';

interface CreateExtensionApplicationForm {
    requestedTerms: FormControl<number>;
    applicationRationale: FormControl<string>;
    delayRationale: FormControl<string>;
    mustPrintDecision: FormControl<boolean>;
}

@Component({
    selector: 'app-study-right-extension-application-form',
    templateUrl: './study-right-extension-application-form.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class StudyRightExtensionApplicationFormComponent {
    @Input({ required: true }) planData: PlanData;
    @Input({ required: true }) automaticallyFilledApplicationFields: AutomaticallyFilledExtensionApplicationFields;
    @Input({ required: true }) continueLabel: string;

    @Output() readonly continue = new EventEmitter<ExtensionApplicationFormData>();
    @Output() readonly exit = new EventEmitter<void>();

    /** Emits `true` after the first edit has been made to the form in the UI. */
    @Output() readonly edited = new EventEmitter<boolean>();

    private readonly fb = inject(SisFormBuilder);
    private readonly transloco = inject(TranslocoService);

    validationDetails: ValidationErrorSummaryDetail[] = [];

    readonly form: FormGroup<CreateExtensionApplicationForm> = this.fb.group({
        requestedTerms: [<number>null, [required(), min(1), max(4)]],
        applicationRationale: [<string>null, [required(), maxLength(MaxLength.MAX_LONG_STRING_LENGTH)]],
        delayRationale: [<string>null, [required(), maxLength(MaxLength.MAX_LONG_STRING_LENGTH)]],
        mustPrintDecision: [<boolean>null, [required()]],
    });

    readonly requestedTermsOptions: Option[] = [
        { value: 1, label: `1 ${this.transloco.translate('PROFILE.EXTENSIONS.SEMESTER')}` },
        { value: 2, label: `2 ${this.transloco.translate('PROFILE.EXTENSIONS.SEMESTERS')}` },
        { value: 3, label: `3 ${this.transloco.translate('PROFILE.EXTENSIONS.SEMESTERS')}` },
        { value: 4, label: `4 ${this.transloco.translate('PROFILE.EXTENSIONS.SEMESTERS')}` },
    ];

    protected readonly getLabelState = getLabelState;

    constructor() {
        this.form.events
            .pipe(
                map(event => event.source.dirty),
                distinctUntilChanged(),
                takeUntilDestroyed(),
            )
            .subscribe(dirty => this.edited.emit(dirty));
    }

    onSubmit() {
        if (this.form.valid) {
            this.continue.emit(<ExtensionApplicationFormData>{ ...this.form.value });
            this.validationDetails = [];
        } else {
            this.form.markAllAsTouched();
            this.validationDetails = this.getValidationErrors();
        }
    }

    private getValidationErrors(): ValidationErrorSummaryDetail[] {
        const errorArr: ValidationErrorSummaryDetail[] = [];
        if (this.form.controls.requestedTerms.invalid) {
            errorArr.push(
                getValidationErrorSummaryDetail(
                    this.form.controls.requestedTerms,
                    this.transloco.translate('STUDENT_APPLICATIONS.EXTENSION_APPLICATION.REQUESTED_TERMS_INPUT_INVALID'),
                    'requested-terms-focus-id',
                ),
            );
        }
        if (this.form.controls.delayRationale.invalid) {
            errorArr.push(
                getValidationErrorSummaryDetail(
                    this.form.controls.delayRationale,
                    this.transloco.translate('STUDENT_APPLICATIONS.EXTENSION_APPLICATION.DELAY_RATIONALE_INPUT_INVALID'),
                    'delay-rationale-focus-id',
                ),
            );
        }
        if (this.form.controls.applicationRationale.invalid) {
            errorArr.push(
                getValidationErrorSummaryDetail(
                    this.form.controls.applicationRationale,
                    this.transloco.translate('STUDENT_APPLICATIONS.EXTENSION_APPLICATION.APPLICATION_RATIONALE_INPUT_INVALID'),
                    'application-rationale-focus-id',
                ),
            );
        }
        if (this.form.controls.mustPrintDecision.invalid) {
            errorArr.push(
                getValidationErrorSummaryDetail(
                    this.form.controls.mustPrintDecision,
                    this.transloco.translate('STUDENT_APPLICATIONS.EXTENSION_APPLICATION.MUST_PRINT_DECISION_INPUT_INVALID'),
                    'must-print-decision-focus-id',
                ),
            );
        }
        return errorArr;
    }
}
